import {Injectable, Inject, Optional} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PixDialogComponent } from './pix-dialog-container/pix-dialog.component';
import {PIX_CONFIG, PixConfig} from '@mydocma/pix-common';
import { MmProjectService } from '../../mm-project/mm-project.service';
import {BehaviorSubject, first, firstValueFrom, from, switchMap} from 'rxjs';
import {Project, ProjectHttpDataService, ProjectService} from '@mydocma/project';
// Update the import path to the correct location for TimeFrame and TimeFrameEnum
import { TimeFrame, TimeFrameEnum } from '@mydocma/pix-common';
import {tap} from 'rxjs/operators';
import {CustomerService} from '@mydocma/customer';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import { ConnectivityService } from '@mydocma/core';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { ArchiveSettings } from '@mydocma/core/src/lib/archive';



export enum FileOriginEnum {
  Pix = 'Pix',
  // ...other origins
}

export interface PixFile extends File {
  id: number;
  title?: string;
  desc?: string;
  tags?: number[];
}


@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class PixService {
  constructor(
    private dialog: MatDialog,
    @Optional() @Inject(PIX_CONFIG) private pixConfig: PixConfig,
    private projectService: ProjectService,
    private projectHttpService: ProjectHttpDataService,
    private customerService: CustomerService,
    private readonly connectivityService: ConnectivityService,
    private readonly dialogService: PromptDialogService,
    @Optional() private readonly archiveSetting: ArchiveSettings,
  ) {
  }

  ensureInit(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if ((this.archiveSetting?.enabled ?? false) || !this.pixConfig?.appId) {
      this.pixAvailableSubject.next(false);
      this.availableProjectsSubject.next([]);
      return;
    }

    this.projectService.currentProject.pipe(untilDestroyed(this),
      tap((project) => this.project = project),
      switchMap(() => from(this.checkAvailableProjects()))).subscribe();
  }

  private initialized = false;
  private project: any;
  private availableProjectsSubject = new BehaviorSubject<Project[]>(null);
  public availableProjects$ = this.availableProjectsSubject.asObservable();
  private pixAvailableSubject = new BehaviorSubject<boolean>(false);
  public pixAvailable$ = this.pixAvailableSubject.asObservable();
  defaultTimeFrame: TimeFrame = {
  selectedMode: TimeFrameEnum.Last_7_Days

};

  pixFSStrategy = {
    text: 'pixDropdown.select',
    icon: { name: '_app-px', outlined: false },
    disabled: false,
    getFiles: this.getFilesPixDialog.bind(this),
    class: 'px-mobile-button-icon'
  };

  private async checkAvailableProjects() {
    try {
      const customer = await firstValueFrom(this.customerService.currentCustomer);
      const projects = await firstValueFrom(this.projectHttpService.getAll(customer.id, this.pixConfig?.appId)) || [];
      this.availableProjectsSubject.next(projects);
    } catch {
      this.availableProjectsSubject.next([]);
    }
  }
  async getFilesPixDialog(): Promise<PixFile[]> {
    if (this.connectivityService.isOfflineVal) {
      await this.dialogService.showOfflineNotificationDialog();
      return [];
    }
    
    const project = await this.projectService.currentProject.pipe(first()).toPromise();
    const mappedProject = { ...project, id: project.id };
    const dialogData = {
      fileOrigin: FileOriginEnum.Pix,
      dialogTitle: 'pix.dialogTitle',
      submitDefaultText: 'pix.importDefault',
      submitActionText: 'pix.importSelection',
      defaultDate: new Date(),
      defaultMode: TimeFrameEnum.Last_7_Days,
      isMobile: false,
      project: mappedProject,
      projectId: mappedProject.id
    };
    const dialogRef = this.dialog.open(PixDialogComponent, {
      panelClass: ['mydocma-dialog', 'files-selection-dialog'],
      disableClose: true,
      autoFocus: false,
      data: dialogData
    });
    const result = await dialogRef.afterClosed().toPromise();
    return result?.selectedFiles || [];
  }
}
